import cls from "classnames";
import { If } from "components";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { t } from "i18next";
import React from "react";

import { PaginationProps } from "./Pagination.types";

import styles from "./Pagination.module.scss";

const MAX_VISIBLE_PAGES = 4;
const MAX_VISIBLE_DOTS = 1;

const Pagination = ({
  currentPage,
  onChange,
  totalItems = 0,
  perPage = GET_LIST_REQUEST_DEFAULT_LIMIT
}: PaginationProps) => {
  const totalPages = Math.ceil(totalItems / perPage);

  const getPageNumbers = (): Array<number | string> => {
    const pageNumbers: Array<number | string> = [];

    if (totalPages <= MAX_VISIBLE_PAGES) {
      // Display all page numbers
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const endPage = MAX_VISIBLE_PAGES;

      if (currentPage <= MAX_VISIBLE_PAGES - MAX_VISIBLE_DOTS) {
        // Show first group of pages
        pageNumbers.push(
          ...Array(endPage)
            .fill(null)
            .map((_, i) => i + 1)
        );
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (
        currentPage >
        totalPages - MAX_VISIBLE_PAGES + MAX_VISIBLE_DOTS
      ) {
        // Show last group of pages
        pageNumbers.push(1);
        pageNumbers.push("...");
        pageNumbers.push(
          ...Array.from(Array(MAX_VISIBLE_PAGES).keys()).map(
            (i) => totalPages - MAX_VISIBLE_PAGES + 1 + i
          )
        );
      } else {
        // Show middle group of pages
        pageNumbers.push(1);
        pageNumbers.push("...");
        const middleStartPage =
          currentPage - Math.floor(MAX_VISIBLE_PAGES / 2) + 1;
        const middleEndPage = currentPage + Math.floor(MAX_VISIBLE_PAGES / 2);
        pageNumbers.push(
          ...Array(middleEndPage - middleStartPage + 1)
            .fill(null)
            .map((_, i) => middleStartPage + i)
        );
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  return (
    <If condition={totalItems > perPage}>
      <div className={styles.pagination}>
        {currentPage !== 1 && (
          <button
            className={cls(
              styles.pagination_button,
              styles.pagination_button_prevNext
            )}
            disabled={currentPage === 1}
            onClick={() => onChange(currentPage - 1)}
          >
            {t("Previous")}
          </button>
        )}

        {getPageNumbers().map((number, index) => (
          <React.Fragment key={index}>
            {number === "..." ? (
              <div
                className={cls(
                  styles.pagination_button,
                  styles.pagination_button_transparent
                )}
              >
                ...
              </div>
            ) : (
              <button
                className={cls(styles.pagination_button, {
                  [styles.pagination_button_active]: number === currentPage
                })}
                onClick={() => onChange(number as number)}
              >
                {number}
              </button>
            )}
          </React.Fragment>
        ))}

        <If condition={currentPage !== totalPages}>
          <button
            className={cls(
              styles.pagination_button,
              styles.pagination_button_prevNext
            )}
            disabled={currentPage === totalPages}
            onClick={() => onChange(currentPage + 1)}
          >
            {t("Next")}
          </button>
        </If>
      </div>
    </If>
  );
};

export default Pagination;
